import { useRouter } from 'next/router'

import useSWR from 'swr'

import { Category, CategoryCollection, ShowcaseCategoryFragment } from '@/generated/contentful'

import { serializeCacheKey } from '@/lib/contentful/utils'

import { Sidebar } from '@/components/Sidebar'

import { getShowcaseCategories } from '../helpers/getShowcaseCategories'

type Props = {
	className?: string
	extraSection?: {
		text: string
		link?: {
			href: string
			target?: '_self' | '_blank'
		}
	}[]
}

export function ShowcaseSidebar({ className, extraSection }: Props) {
	const { locale } = useRouter()

	const { data } = useSWR(
		serializeCacheKey('showcaseCategory', { limit: 100, locale }),
		async () => await getShowcaseCategories(0, 100, locale)
	)

	const items = data?.filter(
		(item) => item?.linkedFrom?.showcaseCollection?.total !== 0
	) as CategoryCollection['items']

	const isCategoryList = (entry?: CategoryCollection['items']): entry is Category[] => {
		if (!entry?.length) return false
		return entry?.every((item) => item?.__typename === 'Category')
	}

	if (!isCategoryList(items)) {
		return null
	}

	const categories = normalizeCategories({ categories: items as ShowcaseCategoryFragment[] })
	return <Sidebar className={className} links={categories} extraSection={extraSection} />
}

type SidebarLink = {
	text: string
	link?: {
		href: string
		target?: '_self' | '_blank'
		childLinks?: SidebarLink[]
	}
}

function normalizeCategories({
	categories
}: {
	categories: ShowcaseCategoryFragment[]
}): SidebarLink[] {
	const showcaseCategory = categories.find((category) => category['slug_en_US'] === 'showcase')

	// Remove the ones that have parents
	const rootCategories =
		categories
			?.filter((category) => !category.parentCategory)
			// Map the parent categories to the sidebar link format
			?.map((parent) => ({
				text: parent.name as string,
				link: {
					href: `/${showcaseCategory?.slug}/${parent.slug}/`,
					childLinks: categories
						// Get the children of the parent
						.filter((item) => item.parentCategory?.slug === parent.slug)
						// Map the children to the sidebar link format
						.map((child) => ({
							text: child.name as string,
							link: {
								href: `/${showcaseCategory?.slug}/${parent.slug}/${child.slug}/`
							}
						}))
						// Sort the children alphabetically
						.sort((a, b) => (a.text ?? '').localeCompare(b.text ?? ''))
				}
			}))
			// Sort the parents alphabetically
			.sort((a, b) => (a.text ?? '').localeCompare(b.text ?? ''))
			// Remove the "Featured" category in order to add it to the beginning of the list
			.filter((item) => item.text !== showcaseCategory?.name) || []

	return [
		{
			text: showcaseCategory?.name ?? 'Featured',
			link: { href: `/${showcaseCategory?.slug}/`, childLinks: [] }
		},
		...rootCategories
	]
}
